import { CAMERA_CONFIG, ERROR_MESSAGES } from '../../utils/constants/appConstants';
import { logger } from '../../utils/logger';
import { safeCanvasToDataURL } from '../../utils/compat';

interface CameraStatus {
  isInitialized: boolean;
  facingMode: string;
  hasStream: boolean;
  hasVideoElement: boolean;
}

/**
 * Local mirror of the DOM `MediaDeviceInfo` shape. Kept as an explicit
 * interface (rather than reusing the lib type) so the public return type of
 * {@link CameraService.getCameraDevices} stays stable regardless of the DOM
 * lib version the project compiles against.
 */
interface MediaDeviceInfo {
  deviceId: string;
  kind: string;
  label: string;
  groupId: string;
}

/* -------------------------------------------------------------------------- */
/*  Typed platform shims                                                       */
/*                                                                            */
/*  These describe non-standard / legacy browser surfaces that the DOM lib    */
/*  does not type. They replace the `any` casts the original used while       */
/*  preserving the exact same runtime checks and call sites.                  */
/* -------------------------------------------------------------------------- */

/** Legacy `navigator.getUserMedia` style callbacks (pre-Promise WebRTC). */
type LegacyGetUserMedia = (
  constraints: MediaStreamConstraints,
  onSuccess: (stream: MediaStream) => void,
  onError: (error: unknown) => void,
) => void;

/** Navigator augmented with the vendor-prefixed getUserMedia variants. */
interface LegacyNavigator extends Navigator {
  getUserMedia?: LegacyGetUserMedia;
  webkitGetUserMedia?: LegacyGetUserMedia;
  mozGetUserMedia?: LegacyGetUserMedia;
}

/**
 * iOS 13+ exposes a static `requestPermission()` on `DeviceOrientationEvent`
 * that must be called from a user gesture before any `deviceorientation`
 * event fires. The DOM lib does not declare it.
 */
type OrientationPermissionState = 'granted' | 'denied' | 'default' | string;
interface DeviceOrientationEventWithPermission {
  requestPermission?: () => Promise<OrientationPermissionState>;
}

/**
 * Minimal shape of the values we read off DOM errors. Browsers throw
 * `DOMException`/`Error` instances here; this lets us read `name`/`message`
 * under strict mode without an `any` cast.
 */
interface CameraErrorLike {
  name?: string;
  message?: string;
}

/** Narrow an unknown thrown value to the fields we log / branch on. */
function asCameraError(error: unknown): CameraErrorLike {
  if (error && typeof error === 'object') {
    const e = error as { name?: unknown; message?: unknown };
    return {
      name: typeof e.name === 'string' ? e.name : undefined,
      message: typeof e.message === 'string' ? e.message : undefined,
    };
  }
  return { message: typeof error === 'string' ? error : undefined };
}

export class CameraService {
  private stream: MediaStream | null;
  private videoElement: HTMLVideoElement | null;
  private isInitialized: boolean;
  private facingMode: string;

  // Physical device orientation in degrees (0/90/180/270) inferred from the
  // gyro. -1 means we have no reading yet. Gyro is the only source that
  // reflects reality when the OS has a portrait rotation lock on — all the
  // screen.orientation/window APIs lie and report 0/portrait in that case.
  private physicalAngle: number = -1;
  private orientationHandler: ((e: DeviceOrientationEvent) => void) | null = null;
  // iOS 13+ requires DeviceOrientationEvent.requestPermission() before any
  // deviceorientation event will fire. Tracked here so we only prompt once
  // per session (the API itself caches the user's choice).
  private orientationPermissionRequested: boolean = false;

  constructor() {
    this.stream = null;
    this.videoElement = null;
    this.isInitialized = false;
    this.facingMode = CAMERA_CONFIG.FACING_MODE.ENVIRONMENT;
  }

  getPhysicalAngle(): number {
    return this.physicalAngle;
  }

  private startOrientationTracking(): void {
    if (typeof window === 'undefined' || this.orientationHandler) return;
    this.orientationHandler = (e: DeviceOrientationEvent) => {
      const beta = e.beta;
      const gamma = e.gamma;
      if (beta === null || gamma === null) return;
      // Quantize to 0/90/180/270.
      // Strong gamma (left/right side low) → landscape.
      // Strong beta (front/back tilt) → portrait.
      // Dead-zone when the phone is flat on a surface keeps the value from
      // flapping.
      if (Math.abs(gamma) > 45 && Math.abs(beta) < 60) {
        this.physicalAngle = gamma > 0 ? 90 : 270;
      } else if (Math.abs(beta) > 45) {
        this.physicalAngle = beta > 0 ? 0 : 180;
      }
    };
    try {
      window.addEventListener('deviceorientation', this.orientationHandler);
    } catch {
      this.orientationHandler = null;
    }
  }

  private stopOrientationTracking(): void {
    if (this.orientationHandler && typeof window !== 'undefined') {
      try {
        window.removeEventListener('deviceorientation', this.orientationHandler);
      } catch {
        /* ignore */
      }
    }
    this.orientationHandler = null;
  }

  /**
   * Ask iOS for permission to receive deviceorientation events. Without this
   * the gyro listener is silent on iOS 13+, capturePhoto falls back to
   * screen.orientation.angle, and a landscape capture under a portrait
   * rotation lock comes out rotated 90°.
   *
   * Safe to call from any user-gesture context; idempotent across the
   * session. On non-iOS or older iOS the permission API doesn't exist and
   * events fire automatically, so this is a no-op there.
   */
  async tryRequestOrientationPermission(): Promise<void> {
    if (this.orientationPermissionRequested) return;
    const DOE: DeviceOrientationEventWithPermission | undefined =
      typeof window !== 'undefined'
        ? (window.DeviceOrientationEvent as unknown as DeviceOrientationEventWithPermission | undefined)
        : undefined;
    if (!DOE || typeof DOE.requestPermission !== 'function') return;

    this.orientationPermissionRequested = true;
    let result: OrientationPermissionState;
    try {
      result = await DOE.requestPermission();
    } catch {
      // Thrown when called outside a user gesture. Allow a retry on the
      // next gesture (e.g. the capture button) instead of giving up.
      this.orientationPermissionRequested = false;
      return;
    }
    if (result !== 'granted') return;

    // Listener is normally already registered by initialize(); call again
    // defensively (it's idempotent via the orientationHandler null-check).
    this.startOrientationTracking();

    // Wait briefly for the first event so the immediately-following capture
    // sees a real angle instead of the -1 sentinel. iOS fires at ~60Hz so
    // 200ms is comfortably more than one frame; bounded to never block.
    const deadline = Date.now() + 200;
    while (this.physicalAngle < 0 && Date.now() < deadline) {
      await new Promise<void>((r) => setTimeout(r, 16));
    }
  }

  async initialize(
    videoElement: HTMLVideoElement,
    facingMode: string = this.facingMode,
    preferredDeviceId: string | null = null,
  ): Promise<boolean> {
    try {
      logger.debug('Initializing camera', { facingMode, preferredDeviceId });
      this.videoElement = videoElement;
      this.facingMode = facingMode;

      // Check browser support
      if (!this.checkBrowserSupport()) {
        logger.error('Browser not supported for camera access');
        throw new Error(ERROR_MESSAGES.CAMERA.NOT_SUPPORTED);
      }

      // Stop any existing stream
      await this.stop();

      logger.debug('Requesting camera access');

      try {
        // Build the video constraints for the ONE getUserMedia of the whole
        // bring-up. When the caller passes the user's previously-chosen lens
        // we add it as a soft `ideal` hint (NOT `exact`): iOS steers to that
        // lens when it's still present and silently degrades to the
        // facingMode default if the id is stale (iOS rotates deviceIds
        // between sessions). This is what lets us restore the preferred lens
        // WITHOUT a second getUserMedia — the second call is what re-showed
        // the camera prompt and, because iOS allows only one active camera,
        // threw NotReadableError and left the feed dead on iPhone.
        const videoConstraints: MediaTrackConstraints = {
          width: { ideal: CAMERA_CONFIG.IDEAL_WIDTH },
          height: { ideal: CAMERA_CONFIG.IDEAL_HEIGHT },
          facingMode: this.facingMode,
        };
        if (preferredDeviceId) {
          videoConstraints.deviceId = { ideal: preferredDeviceId };
        }

        // Request camera access directly without timeout
        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: false,
        });

        logger.debug('Camera access granted', { streamId: stream.id, streamActive: stream.active });
        this.stream = stream;

        // NOTE: do NOT request DeviceOrientationEvent permission here.
        //
        // iOS shows a *separate* system prompt for motion/orientation, and
        // chaining it right after the camera grant produced the reported
        // iPhone bug: the user accepts the camera prompt, immediately gets a
        // second prompt ("the same question again"), and the camera then
        // fails to come up. Two reasons:
        //   1. requestPermission() must run inside a live user gesture. Called
        //      after `await getUserMedia` the activation is already spent, so
        //      iOS rejects it.
        //   2. awaiting a system dialog here blocks stream setup while the
        //      dialog is open, which on iOS can mute/end the just-acquired
        //      track — leaving the feed dead.
        // The gyro permission is instead requested from a real gesture, the
        // capture button (see cameraStore.capturePhoto), which is the only
        // context iOS actually honours. We still start the passive listener
        // below; it stays silent until permission is granted there.

        // Set video source
        if (this.videoElement) {
          logger.debug('Setting video source');
          this.videoElement.srcObject = stream;

          logger.debug('Video element after setting stream', {
            srcObject: !!this.videoElement.srcObject,
            readyState: this.videoElement.readyState,
            videoWidth: this.videoElement.videoWidth,
            videoHeight: this.videoElement.videoHeight,
            clientWidth: this.videoElement.clientWidth,
            clientHeight: this.videoElement.clientHeight,
          });

          // Play the video
          try {
            logger.debug('Playing video');
            await this.videoElement.play();
            logger.debug('Video playing', {
              readyState: this.videoElement.readyState,
              videoWidth: this.videoElement.videoWidth,
              videoHeight: this.videoElement.videoHeight,
              paused: this.videoElement.paused,
            });
          } catch (playError) {
            logger.warn('Error playing video — may need user interaction', {
              error: asCameraError(playError).message,
            });
            // Continue anyway, some browsers need user interaction
          }
        } else {
          logger.error('No video element available');
          throw new Error('No video element available');
        }

        this.isInitialized = true;
        this.startOrientationTracking();
        logger.info('Camera initialized successfully');
        return true;
      } catch (accessError: unknown) {
        const { message } = asCameraError(accessError);
        logger.error('Camera access error', { error: message });
        const errorMessage = this.handleCameraError(accessError);
        throw new Error(errorMessage);
      }
    } catch (error: unknown) {
      const { message } = asCameraError(error);
      logger.error('Camera initialization error', { error: message });
      const errorMessage = this.handleCameraError(error);
      throw new Error(errorMessage);
    }
  }

  async stop(): Promise<void> {
    this.stopOrientationTracking();
    if (this.stream) {
      try {
        this.stream.getTracks().forEach((track) => {
          track.stop();
        });
      } catch (error: unknown) {
        logger.error('Error stopping camera tracks', { error: asCameraError(error).message });
      }
      this.stream = null;
    }

    if (this.videoElement) {
      try {
        this.videoElement.srcObject = null;
        this.videoElement.load();
      } catch (error: unknown) {
        logger.error('Error resetting video element', { error: asCameraError(error).message });
      }
    }

    this.isInitialized = false;
  }

  async capturePhoto(canvasElement: HTMLCanvasElement): Promise<string> {
    if (!this.isInitialized || !this.videoElement || !canvasElement) {
      throw new Error('Camera not ready for photo capture');
    }

    if (this.videoElement.videoWidth === 0 || this.videoElement.videoHeight === 0) {
      throw new Error('Camera is still loading');
    }

    const context = canvasElement.getContext('2d');
    if (!context) {
      throw new Error('Canvas context not available');
    }

    const vw = this.videoElement.videoWidth;
    const vh = this.videoElement.videoHeight;

    // Decide the TARGET orientation from the APP's OWN VIEWPORT — i.e. exactly
    // what the user sees on screen. window.innerWidth/innerHeight always reflect
    // the rendered layout, so if the app is showing landscape, the saved image
    // MUST be landscape — full stop.
    //
    // This deliberately does NOT key off screen.orientation (absent on older
    // iOS Safari, and frozen to "portrait" under a rotation lock) nor the gyro
    // (physicalAngle goes stale when the phone is held flat to shoot a floor or
    // ceiling — its axes fall in the dead-zone, see startOrientationTracking).
    // Both of those misread the orientation in the field and were rotating
    // landscape frames into portrait images. The viewport can't be fooled: it
    // IS the orientation the user is looking at.
    //
    // The orientation ANGLE is consulted only to pick the rotation direction
    // (CW vs CCW) when a rotation is actually needed — never to infer
    // landscape-vs-portrait.
    let needsRotation = false;
    let screenAngle = 0;
    try {
      const videoLandscape = vw > vh;
      const appLandscape = window.innerWidth > window.innerHeight;
      needsRotation = appLandscape !== videoLandscape;

      // Rotation direction only. Prefer the gyro (true physical angle), then
      // screen.orientation.angle, then the legacy window.orientation
      // (-90 ⇒ 270). Defaults to 0 (⇒ clockwise) when nothing is available.
      const gyroAngle = this.physicalAngle;
      const screenOrientationAngle = window.screen?.orientation?.angle;
      const legacyAngle = (window as Window & { orientation?: number }).orientation;
      if (gyroAngle >= 0) {
        screenAngle = gyroAngle;
      } else if (typeof screenOrientationAngle === 'number') {
        screenAngle = screenOrientationAngle;
      } else if (typeof legacyAngle === 'number') {
        screenAngle = legacyAngle < 0 ? legacyAngle + 360 : legacyAngle;
      }
    } catch {
      // viewport / orientation APIs unavailable — keep the native frame orientation.
    }

    if (needsRotation) {
      // Rotated: swap width/height and apply 90° rotation to correct orientation.
      // Canvas becomes (vh × vw) — dimensions swapped to match the target orientation.
      canvasElement.width = vh;
      canvasElement.height = vw;
      context.save();
      context.translate(vh / 2, vw / 2);
      // Rotation direction:
      //   portrait phone (angle=0) with landscape video → rotate CW (+π/2)
      //   landscape-left phone (angle=270) with portrait video → rotate CW (+π/2)
      //   landscape-right phone (angle=90) with portrait video → rotate CCW (-π/2)
      const rotateCW = screenAngle !== 90;
      context.rotate(rotateCW ? Math.PI / 2 : -Math.PI / 2);
      context.drawImage(this.videoElement, -vw / 2, -vh / 2, vw, vh);
      context.restore();
    } else {
      // Normal: frame already matches screen orientation
      canvasElement.width = vw;
      canvasElement.height = vh;
      context.drawImage(this.videoElement, 0, 0, vw, vh);
    }

    // Convert to data URL. safeCanvasToDataURL falls back to PNG instead of
    // throwing/returning empty if iOS Safari fails to JPEG-encode the frame.
    const photoData = safeCanvasToDataURL(canvasElement, 'image/jpeg', 0.9);
    return photoData;
  }

  async flipCamera(): Promise<string> {
    const newFacingMode =
      this.facingMode === CAMERA_CONFIG.FACING_MODE.ENVIRONMENT
        ? CAMERA_CONFIG.FACING_MODE.USER
        : CAMERA_CONFIG.FACING_MODE.ENVIRONMENT;

    if (this.videoElement) {
      await this.initialize(this.videoElement, newFacingMode);
    } else {
      throw new Error('No video element available for camera flip');
    }

    return newFacingMode;
  }

  async getCameraDevices(): Promise<MediaDeviceInfo[]> {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      return devices.filter((device) => device.kind === 'videoinput') as MediaDeviceInfo[];
    } catch (error: unknown) {
      logger.error('Error enumerating camera devices', { error: asCameraError(error).message });
      return [];
    }
  }

  /**
   * deviceId of the lens currently feeding the live <video>, or null when no
   * stream is up. Read from the active track's settings (populated once the
   * stream is live), so it reflects what the browser ACTUALLY opened — which
   * may differ from a requested `ideal` hint if that lens was unavailable.
   */
  getActiveDeviceId(): string | null {
    try {
      const track = this.stream?.getVideoTracks()[0];
      return track?.getSettings().deviceId ?? null;
    } catch {
      return null;
    }
  }

  async switchToDevice(deviceId: string): Promise<boolean> {
    // iOS allows only ONE active camera capture at a time. Acquiring the new
    // stream BEFORE releasing the old one briefly holds two streams, which on
    // iPhone throws NotReadableError (and can kill the existing feed). Stop
    // first, then acquire. The tradeoff is a short black frame during the
    // switch, which is acceptable for an explicit lens change.
    await this.stop();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: { exact: deviceId },
          width: { ideal: CAMERA_CONFIG.IDEAL_WIDTH },
          height: { ideal: CAMERA_CONFIG.IDEAL_HEIGHT },
        },
        audio: false,
      });

      this.stream = stream;

      if (this.videoElement) {
        this.videoElement.srcObject = stream;
        await this.waitForVideoReady();
      }

      this.isInitialized = true;
      return true;
    } catch (error: unknown) {
      this.handleCameraError(error);
      throw error;
    }
  }

  private checkBrowserSupport(): boolean {
    try {
      // Check if we're in a browser environment
      if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        logger.warn('Not in browser environment');
        return false;
      }

      // Check if mediaDevices is available
      if (!navigator.mediaDevices) {
        // Try to polyfill mediaDevices for older browsers
        const legacyNavigator = navigator as LegacyNavigator;
        const legacyGetUserMedia =
          legacyNavigator.getUserMedia ||
          legacyNavigator.webkitGetUserMedia ||
          legacyNavigator.mozGetUserMedia;

        if (legacyGetUserMedia) {
          (legacyNavigator as unknown as { mediaDevices: { getUserMedia: (c: MediaStreamConstraints) => Promise<MediaStream> } }).mediaDevices = {
            getUserMedia: (constraints: MediaStreamConstraints): Promise<MediaStream> => {
              const getUserMedia =
                legacyNavigator.getUserMedia ||
                legacyNavigator.webkitGetUserMedia ||
                legacyNavigator.mozGetUserMedia;
              return new Promise<MediaStream>((resolve, reject) => {
                getUserMedia!.call(navigator, constraints, resolve, reject);
              });
            },
          };
        } else {
          logger.warn('navigator.mediaDevices not available');
          return false;
        }
      }

      // Check if getUserMedia is available
      if (!navigator.mediaDevices.getUserMedia) {
        logger.warn('getUserMedia not available');
        return false;
      }

      // Check if we're in a secure context (HTTPS or localhost)
      if (
        window.location.protocol !== 'https:' &&
        window.location.hostname !== 'localhost' &&
        !window.location.hostname.includes('192.168.')
      ) {
        logger.warn('Camera requires HTTPS or localhost');
        return false;
      }

      return true;
    } catch (error: unknown) {
      logger.error('Error checking browser support', { error: asCameraError(error).message });
      return false;
    }
  }

  private async requestCameraAccess(): Promise<MediaStream> {
    return navigator.mediaDevices.getUserMedia({
      video: {
        width: { ideal: CAMERA_CONFIG.IDEAL_WIDTH },
        height: { ideal: CAMERA_CONFIG.IDEAL_HEIGHT },
        facingMode: this.facingMode,
      },
      audio: false,
    });
  }

  private async waitForVideoReady(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.videoElement) {
        reject(new Error('Video element not available'));
        return;
      }

      const timeout = setTimeout(() => {
        reject(new Error('Video ready timeout'));
      }, CAMERA_CONFIG.TIMEOUT);

      const handleCanPlay = (): void => {
        clearTimeout(timeout);
        this.videoElement!.removeEventListener('canplay', handleCanPlay);
        this.videoElement!.removeEventListener('error', handleError);

        this.videoElement!.play().then(resolve).catch(reject);
      };

      const handleError = (error: Event): void => {
        clearTimeout(timeout);
        this.videoElement!.removeEventListener('canplay', handleCanPlay);
        this.videoElement!.removeEventListener('error', handleError);
        reject(error);
      };

      this.videoElement.addEventListener('canplay', handleCanPlay, { once: true });
      this.videoElement.addEventListener('error', handleError, { once: true });
    });
  }

  private handleCameraError(error: unknown): string {
    const { name, message: rawMessage } = asCameraError(error);
    let message: string = ERROR_MESSAGES.CAMERA.INITIALIZATION_FAILED;

    if (name === 'NotAllowedError') {
      message = ERROR_MESSAGES.CAMERA.PERMISSION_DENIED;
    } else if (name === 'NotFoundError') {
      message = ERROR_MESSAGES.CAMERA.NOT_FOUND;
    } else if (name === 'NotReadableError') {
      message = ERROR_MESSAGES.CAMERA.IN_USE;
    } else if (name === 'OverconstrainedError') {
      message = 'Camera constraints not supported';
    } else if (name === 'TypeError') {
      message = ERROR_MESSAGES.CAMERA.NOT_SUPPORTED;
    } else if (rawMessage && rawMessage.includes('timeout')) {
      message = 'Camera initialization timed out. Please try again.';
    } else if (rawMessage && rawMessage.includes('HTTPS')) {
      message = 'Camera requires HTTPS. Please access via localhost or enable HTTPS.';
    }

    logger.error('Camera error', { name, message: rawMessage });
    return message;
  }

  getStatus(): CameraStatus {
    return {
      isInitialized: this.isInitialized,
      facingMode: this.facingMode,
      hasStream: !!this.stream,
      hasVideoElement: !!this.videoElement,
    };
  }

  getStream(): MediaStream | null {
    return this.stream;
  }

  /**
   * Point the service at a (possibly new) <video> element and rebind the
   * current stream to it WITHOUT re-acquiring the camera.
   *
   * This is what keeps iOS from re-showing the camera permission prompt: the
   * BackgroundCamera component unmounts/remounts as the user moves between
   * camera-bearing and non-camera screens, and a fresh getUserMedia on every
   * remount makes iOS Safari pop the permission sheet again (and frequently
   * error out on the rapid re-acquire). Instead we leave the stream alive and
   * just re-attach it to the freshly-mounted element here. Also updates the
   * element the capture path draws from, so capturePhoto never reads a stale,
   * detached <video>. No-op-safe when there's no stream yet.
   */
  setVideoElement(el: HTMLVideoElement): void {
    this.videoElement = el;
    if (this.stream) {
      try {
        el.srcObject = this.stream;
      } catch (error: unknown) {
        logger.warn('Failed to re-attach stream to video element', {
          error: asCameraError(error).message,
        });
      }
    }
  }
}

// Singleton instance
const cameraService = new CameraService();
export default cameraService;
